<template>
	<div>
		<div class="flex wrap xs-12 xs-center xs-top files-upload-full-height">
			<div class="xs-12" v-if="files.length">
				<div class="flex xs-12 lh150 fs3">
					{{ files[0].name }}
				</div>
				<div class="flex xs-12 c6 fs3 pt2 pb2">
					<strong>{{ $t("location") }}</strong
					>: {{ location }}
				</div>
			</div>

			<div class="flex xs-left xs-12 pb4 pl2 fs3 c6" v-if="!files.length">
				{{ $t("formats_accepted") }} : {{ supportedFileTypes.join(", ") }}
			</div>

			<!-- Upload -->
			<vue-upload-component
				class="flex xs-12 xs-center p2 bg5"
				:input-id="inputId"
				:ref="inputId"
				v-model="files"
				:post-action="getUrl"
				:headers="headers"
				:data="getBody"
				:drop="true"
				:extensions="
					supportedFileTypes
						? supportedFileTypes
						: $store.state.settings.filesUpload.types
				"
				:size="$store.state.settings.filesUpload.maxFileSize"
				@input-file="inputFile"
				@input-filter="inputFilter"
			>
				<!-- Show when there are no files -->
				<template v-if="!files.length">
					<div class="flex wrap xs-12 xs-center b2 p15 bc7">
						<j-text class="xs-12 fs5 c6 fw7 mb8">
							{{ $t("drag_your_files") }}
						</j-text>
						<j-button type="bordered" size="medium">
							{{ $t("select_files") }}
						</j-button>
					</div>
				</template>
				<!-- We cannot hide vue-upload-component if !files.length or the file is not uploaded -->
				<div v-else class="xs-12">
					<j-button type="tertiary">{{ $t("update_image") }}</j-button>
				</div>
			</vue-upload-component>
		</div>
	</div>
</template>

<script>
import JText from "@/components/JText/"
import JButton from "@/components/JButton/"

// We use 3rd party component for upload:
// https://lian-yue.github.io/vue-upload-component/#/en/documents
import VueUploadComponent from "vue-upload-component"

/**
 * This component is resposible for uploading of files
 * When upload is finished, component emits event "upload:finished"
 */
export default {
	components: {
		JText,
		JButton,
		VueUploadComponent
	},

	props: {
		/**
		 * Id of course we are uploading file to
		 */
		courseId: {
			type: String,
			default: ""
		},

		/**
		 * used by the v-model
		 * key of the file uploaded
		 */
		value: {
			type: String,
			default: ""
		},

		/**
		 * Has to be unique in case we have several times this component on the same view
		 */
		inputId: {
			type: String,
			default: ""
		},

		// optional
		supportedFileTypes: {
			type: Array,
			required: false
		},

		isPublic: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			// file to upload
			files: [],

			// Auth
			headers: {
				Authorization: `Bearer ${window.TK}`
			}
		}
	},

	computed: {
		/**
		 * Return proper endpoint for upload based on filesOf
		 */
		getUrl() {
			return `https://api.asia.covergo.com/api/v1/files/${encodeURIComponent(
				this.location
			)}`
		},

		location() {
			return `education/${this.courseId}/`
		},

		getBody() {
			if (this.isPublic) {
				return {
					isPublic: true
				}
			} else return null
		}
	},

	methods: {
		/**
		 * Will trigger upload of files
		 */
		onSubmit() {
			this.$refs[this.inputId].active = true
		},

		/**
		 * Watch files
		 */
		inputFile: function(newFile, oldFile) {
			// A file has been added -> upload
			if (newFile && !newFile.active && !oldFile) {
				this.onSubmit()
				return
			}
			// After upload, check response
			if (newFile && oldFile && !newFile.active && oldFile.active) {
				// Check if extension is wrong
				if (newFile.error && newFile.error === "extension") {
					this.$store.dispatch(
						"addMessage",
						{
							message: {
								type: "error",
								text: this.$t("errors.file_extension")
							}
						},
						{ root: true }
					)
					this.files = []
					return
				}
				// Check file size error
				if (newFile.error && newFile.error === "size") {
					this.$store.dispatch(
						"addMessage",
						{
							message: {
								type: "error",
								text: this.$t("errors.file_size")
							}
						},
						{ root: true }
					)
					this.files = []
					return
				}

				// Get response data
				if (newFile.xhr) {
					if (newFile.xhr.status === 401) {
						// If 401 - show message that the user is not authorized to do this action
						this.$store.dispatch(
							"addMessage",
							{
								message: {
									type: "error",
									text: this.$t("errors.authorization")
								}
							},
							{ root: true }
						)
					} else if (newFile.xhr.status > 401) {
						// If different error, show general error
						this.$store.dispatch(
							"addMessage",
							{
								message: {
									type: "error",
									text: this.$t("errors.general")
								}
							},
							{ root: true }
						)
					} else {
						// Check if file already exists
						const res = JSON.parse(newFile.xhr.response)

						// If file already exists in the backend, show error
						if (
							res.errors &&
							res.errors.includes(
								"Specified Object Already Exists in the Bucket"
							)
						) {
							this.$store.dispatch(
								"addMessage",
								{
									message: {
										type: "warning",
										text: this.$t("errors.file_already_uploaded")
									}
								},
								{ root: true }
							)
						}
					}
				}
			}
		},

		/**
		 * Filter out some specific files like system files and hidden files
		 */
		inputFilter: function(newFile, oldFile, prevent) {
			if (newFile && !oldFile) {
				// Filter system files or hidden files
				if (/(\/|^)(Thumbs\.db|desktop\.ini|\..+)$/.test(newFile.name)) {
					return prevent()
				}
			}
		}
	},

	watch: {
		value: {
			handler(newVal) {
				this.files = newVal
					? [{ name: newVal.split("/")[newVal.split("/").length - 1] }]
					: []
			},
			immediate: true
		}
	},

	/**
	 * Here we have to set a special watcher that watches $refs
	 * $refs is not available before mount, so this has to happen in mount
	 * The reason is file component is returning this.$refs.upload.uploaded = true
	 * when all uploads finsihed, we need to catch that and emit event to parent
	 */
	mounted() {
		this.$watch(
			() => {
				return this.$refs[this.inputId] && this.$refs[this.inputId].uploaded
			},
			val => {
				if (val === true) {
					this.$emit("input", this.location + this.files[0]?.name)
					this.$emit("blur")
				}
			}
		)
	},

	filters: {
		/**
		 * Show proper file size
		 */
		fileSize(a, b) {
			if (0 == a) return "0 Bytes"
			let c = 1024,
				d = b || 2,
				e = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"],
				f = Math.floor(Math.log(a) / Math.log(c))
			return parseFloat((a / Math.pow(c, f)).toFixed(d)) + " " + e[f]
		}
	}
}
</script>

<style scoped>
.files-upload-full-height {
	min-height: 100%;
}
</style>
